import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"

import Home from "./pages/Home"
import MatchDetails from "./pages/MatchDetails"
import Leagues from "./pages/Leagues"
import ContestPage from "./pages/ContestList"
import CreateTeam from "./pages/CreateTeam"
import CaptainPage from "./pages/CaptainPage"
import MyTeams from "./pages/MyTeams"
import Leaderboard from "./pages/Leaderboard"
import Profile from "./pages/Profile"
import MyContests from "./pages/MyContests"
import Login from "./pages/Login"
import ProtectedRoute from "./components/ProtectedRoute"
import PublicRoute from "./components/PublicRoute"
import Splash from "./pages/Splash"
import Wallet from "./pages/Wallet"
import BankDetails from "./pages/BankDetails"
import Withdrawal from "./pages/Withdrawal"
import HelpSupport from "./pages/HelpSupport"
/* Legal Imports */
import { Terms, Privacy, FairPlay } from "./pages/LegalPages"
import PlayerPerformance from "./pages/PlayerPerformance"
import PlayerContests from "./pages/PlayerContests"
import PlayerLeaderboard from "./pages/PlayerLeaderboard"
import OfflineOverlay from "./components/OfflineOverlay"
import { ThemeProvider } from "./context/ThemeContext"
import { GlobalToastProvider } from "./components/GlobalToast"
import UpdateModal from "./components/UpdateModal"

// Admin Pages - trigger rebuild
import AdminLogin from "./pages/Admin/AdminLogin"
import AdminDashboard from "./pages/Admin/AdminDashboard"
import AddPlayer from "./pages/Admin/AddPlayer"
import UpdatePoints from "./pages/Admin/UpdatePoints"
import AnnouncePlaying11 from "./pages/Admin/AnnouncePlaying11"
import RevenueAnalytics from "./pages/Admin/RevenueAnalytics"
import ManageContests from "./pages/Admin/ManageContests"
import ManageMatchPlayers from "./pages/Admin/ManageMatchPlayers"
import UpdateScore from "./pages/Admin/UpdateScore"
import GoaLeaderboard from "./pages/Admin/GoaLeaderboard"
import AdminUsers from "./pages/Admin/AdminUsers"
import UserGoaLeaderboard from "./pages/GoaLeaderboard"
import ManagePlayerTemplates from "./pages/Admin/ManagePlayerTemplates"
import CompletedMatches from "./pages/Admin/CompletedMatches"
import ManageDeposits from "./pages/Admin/ManageDeposits"
import ManageWithdrawals from "./pages/Admin/ManageWithdrawals"
import ManageTeams from "./pages/Admin/ManageTeams"
import TeamSquad from "./pages/Admin/TeamSquad"
import MatchPanel from "./pages/Admin/MatchPanel"
import AdminUserActivity from "./pages/Admin/AdminUserActivity"
import AppSettings from "./pages/Admin/AppSettings"
import ManageAdmins from "./pages/Admin/ManageAdmins"
import ManageBanners from "./pages/Admin/ManageBanners"
import WinnerControl from "./pages/Admin/WinnerControl"


// Admin Pages - trigger rebuild

function App() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Anti-Screenshot & Anti-Recording Logic
    const preventCopying = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeydown = (e) => {
      // Block PrintScreen
      if (e.key === "PrintScreen" || e.keyCode === 44) {
        navigator.clipboard.writeText(""); // Clear clipboard
        alert("Screenshots are not allowed for security reasons.");
      }
      // Block Ctrl+P, Ctrl+S, MacOS Cmd+Shift+3/4
      if (
        (e.ctrlKey && (e.key === "p" || e.key === "s")) ||
        (e.metaKey && e.shiftKey)
      ) {
        e.preventDefault();
      }
    };

    const handleBlur = () => {
      document.body.style.filter = "blur(15px)";
      document.body.style.opacity = "0.5";
    };

    const handleFocus = () => {
      document.body.style.filter = "none";
      document.body.style.opacity = "1";
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // DRM Listeners
    window.addEventListener('contextmenu', preventCopying); // Block right click
    window.addEventListener('dragstart', preventCopying); // Block image drag
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('blur', handleBlur); // Blur on focus lost
    window.addEventListener('focus', handleFocus); // Unblur on focus

    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      console.log("🎁 Referral ID captured:", ref);
      sessionStorage.setItem("winrox11_referrer", ref);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('contextmenu', preventCopying);
      window.removeEventListener('dragstart', preventCopying);
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return (
    <GlobalToastProvider>
      <UpdateModal />
      <ThemeProvider>
        <BrowserRouter>
          {isOffline && <OfflineOverlay />}
          <Routes>
            {/* ... routes ... */}
            <Route path="/" element={<Splash />} />

            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />

            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/match/:id" element={
              <ProtectedRoute>
                <MatchDetails />
              </ProtectedRoute>
            } />
            <Route path="/Leagues" element={
              <ProtectedRoute>
                <Leagues />
              </ProtectedRoute>
            } />
            <Route path="/contest/:matchId" element={
              <ProtectedRoute>
                <ContestPage />
              </ProtectedRoute>
            } />
            <Route path="/create-team/:matchId" element={
              <ProtectedRoute>
                <CreateTeam />
              </ProtectedRoute>
            } />
            <Route path="/captain" element={
              <ProtectedRoute>
                <CaptainPage />
              </ProtectedRoute>
            } />
            <Route path="/my-teams" element={
              <ProtectedRoute>
                <MyTeams />
              </ProtectedRoute>
            } />
            <Route path="/leaderboard/:contestId" element={
              <ProtectedRoute>
                <Leaderboard />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/my-contests" element={
              <ProtectedRoute>
                <MyContests />
              </ProtectedRoute>
            } />
            <Route path="/wallet" element={
              <ProtectedRoute>
                <Wallet />
              </ProtectedRoute>
            } />
            <Route path="/bank-details" element={
              <ProtectedRoute>
                <BankDetails />
              </ProtectedRoute>
            } />
            <Route path="/withdrawal" element={
              <ProtectedRoute>
                <Withdrawal />
              </ProtectedRoute>
            } />
            <Route path="/help" element={
              <ProtectedRoute>
                <HelpSupport />
              </ProtectedRoute>
            } />
            <Route path="/terms" element={<ProtectedRoute><Terms /></ProtectedRoute>} />
            <Route path="/privacy" element={<ProtectedRoute><Privacy /></ProtectedRoute>} />
            <Route path="/fairplay" element={<ProtectedRoute><FairPlay /></ProtectedRoute>} />
            <Route path="/player-performance" element={
              <ProtectedRoute>
                <PlayerPerformance />
              </ProtectedRoute>
            } />
            <Route path="/player-contests" element={
              <ProtectedRoute>
                <PlayerContests />
              </ProtectedRoute>
            } />
            <Route path="/player-leaderboard/:challengeId" element={
              <ProtectedRoute>
                <PlayerLeaderboard />
              </ProtectedRoute>
            } />
            <Route path="/goa-leaderboard" element={
              <ProtectedRoute>
                <UserGoaLeaderboard />
              </ProtectedRoute>
            } />

            {/* Admin Redirect for ease of access */}
            <Route path="/admin" element={<Navigate to="/winrox11-super-admin" replace />} />

            {/* --- HIDDEN ADMIN ROUTES --- */}
            <Route path="/winrox11-super-admin" element={<AdminLogin />} />
            <Route path="/winrox11-super-admin/dashboard" element={<AdminDashboard />} />
            <Route path="/winrox11-super-admin/completed-matches" element={<CompletedMatches />} />
            <Route path="/winrox11-super-admin/add-player" element={<AddPlayer />} />
            <Route path="/winrox11-super-admin/update-points" element={<UpdatePoints />} />
            <Route path="/winrox11-super-admin/playing-11" element={<AnnouncePlaying11 />} />
            <Route path="/winrox11-super-admin/revenue" element={<RevenueAnalytics />} />
            <Route path="/winrox11-super-admin/manage-contests" element={<ManageContests />} />
            <Route path="/winrox11-super-admin/manage-player-templates" element={<ManagePlayerTemplates />} />
            <Route path="/winrox11-super-admin/manage-match-players" element={<ManageMatchPlayers />} />
            <Route path="/winrox11-super-admin/update-score/:matchId" element={<UpdateScore />} />
            <Route path="/winrox11-super-admin/goa-leaderboard" element={<GoaLeaderboard />} />
            <Route path="/winrox11-super-admin/users" element={<AdminUsers />} />
            <Route path="/winrox11-super-admin/manage-deposits" element={<ManageDeposits />} />
            <Route path="/winrox11-super-admin/manage-withdrawals" element={<ManageWithdrawals />} />
            <Route path="/winrox11-super-admin/manage-teams" element={<ManageTeams />} />
            <Route path="/winrox11-super-admin/team-squad/:shortCode" element={<TeamSquad />} />
            <Route path="/winrox11-super-admin/match/:matchId" element={<MatchPanel />} />
            <Route path="/winrox11-super-admin/user-activity" element={<AdminUserActivity />} />
            <Route path="/winrox11-super-admin/app-settings" element={<AppSettings />} />
            <Route path="/winrox11-super-admin/manage-admins" element={<ManageAdmins />} />
            <Route path="/winrox11-super-admin/manage-banners" element={<ManageBanners />} />
            <Route path="/winrox11-super-admin/winner-control" element={<WinnerControl />} />



          </Routes>

        </BrowserRouter>
      </ThemeProvider>
    </GlobalToastProvider>
  )
}

// Add global styles for DRM
const drmStyles = document.createElement("style");
drmStyles.innerHTML = `
  /* Webkit pointer events fix for images */
  img { pointer-events: none; -webkit-user-drag: none; }
`;
document.head.appendChild(drmStyles);

export default App